import { useTheme } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { VALIDATOR_TYPE_IMPOTS, useSelectedDossierContext } from '@europrocurement/l2d-modules';
import { Accordion, ConfirmationModal, useModal } from '@europrocurement/flexy-components';
import { enqueueSnackbar } from 'notistack';
import { useHandleError } from '@europrocurement/l2d-hooks';
import {
    AddArticleParamsType,
    DossierArticle,
    useDossierArticle,
    useDossierProcesses,
    useDossierProcessesActionsAndRender,
} from '../../hooks';
import { DossierArticleProcessMenuItemProps } from '../DossierArticleProcessMenu/DossierArticleProcessMenu';
import DossierProcessesFooter from '../DossierProcessesFooter/DossierProcessesFooter';
import AddTaxForm, { AddTaxFormData } from '../AddTaxForm/AddTaxForm';
import UpdateValidatorForm from '../UpdateValidatorForm/UpdateValidatorForm';
// import MultipleArticleUpdateValidatorForm from '../MultipleArticleUpdateValidatorForm/MultipleArticleUpdateValidatorForm';
import DossierArticlesProcessesTaxList from '../DossierArticlesProcessesTaxList/DossierArticlesProcessesTaxList';
import { DossierProcessesArticlesProps } from '../DossierProcesses/DossierProcessesType';

const DossierImpotProcesses: React.FunctionComponent<DossierProcessesArticlesProps> = function ({
    articles,
}) {
    const validatorTypeId = VALIDATOR_TYPE_IMPOTS;
    const {
        getArticleProcessesStatus,
        getArticleProcessesStatusLabel,
        getArticleProcessesStatusColor,
        findProcessByArticle,
        canCreateValidatorProcess,
        createValidatorProcess,
        getProcessStatusByArticle,
    } = useDossierProcesses();
    const { palette } = useTheme();
    const { addArticle, deleteArticle } = useDossierArticle();
    const { dossier, refreshDossier } = useSelectedDossierContext();
    const { modalActions } = useModal();
    const { apiErrorMessageSnackbar } = useHandleError();

    const [processing, setProcessing] = useState<boolean>(false);
    const status = getArticleProcessesStatus(
        dossier,
        validatorTypeId,
        articles.map((article) => article.id),
    );

    const { actionUpdateValidatorStatus } = useDossierProcessesActionsAndRender();
    const handelDeleteArticle = (articleId: string) => {
        setProcessing(true);

        deleteArticle(articleId)
            .then(() => {
                setProcessing(false);
                modalActions.close();
            })
            .catch((err) =>
                apiErrorMessageSnackbar(
                    err,
                    'Une erreur est survenue lors de la suppression de la démarche',
                ),
            );

        setProcessing(false);
        refreshDossier();
    };

    const canCreateProcess = canCreateValidatorProcess(dossier, validatorTypeId);

    const getItemActions = (item: DossierArticle) => {
        const actions: Array<DossierArticleProcessMenuItemProps> = [];
        const statusProcess = getProcessStatusByArticle(dossier, validatorTypeId, item.id);

        // actions.push({
        //     label: 'Dissocier',
        //     icon: 'minus-square',
        //     onClick: (article: DossierArticle) => {
        //         enqueueSnackbar(`TODO dissocier ${article.label}`, { variant: 'error' });
        //     },
        // });

        // actions.push({
        //     label: 'Déplacer vers un autre dossier',
        //     icon: 'folders',
        //     onClick: (article: DossierArticle) => {
        //         modalActions.call(
        //             <MoveArticleToOtherDossierForm
        //                 processing={processing}
        //                 article={article}
        //                 dossier={dossier}
        //                 onItemSelected={() => {
        //                     modalActions.close();
        //                     refreshDossier();
        //                 }}
        //             />,
        //         );
        //     },
        // });

        if (['pending'].includes(statusProcess) && canCreateProcess) {
            actions.push(
                actionUpdateValidatorStatus(() => {
                    const process = findProcessByArticle(dossier, [validatorTypeId], item.id);
                    let currentStatus = { value: '', label: '' };
                    if (process) {
                        currentStatus = {
                            value: process.statusCode,
                            label: process.statusLabel,
                        };
                    }
                    modalActions.call(
                        <UpdateValidatorForm
                            validatorTypeId={validatorTypeId}
                            processing={processing}
                            article={item}
                            dossier={dossier}
                            onItemSelected={() => {
                                modalActions.close();
                                refreshDossier();
                            }}
                            currentStatus={currentStatus}
                        />,
                    );
                }),
            );

            actions.push({
                label: 'Supprimer la démarche',
                icon: 'trash',
                onClick: (article: DossierArticle) => {
                    modalActions.call(
                        <ConfirmationModal
                            color={palette.error}
                            messages={{
                                question: 'Confirmer cette action ?',
                                detail: {
                                    message: 'Êtes vous sur de vouloir supprimer la démarche ?',
                                    severity: 'error',
                                },
                                validation: 'Supprimer',
                            }}
                            actionOnValidation={() => handelDeleteArticle(article.id.toString())}
                            actionOnCancellation={() => modalActions.close()}
                        />,
                    );
                },
            });
        } else if (['ongoing', 'default', 'rescheduled'].includes(statusProcess)) {
            actions.push({
                label: 'Demander une signature électronique',
                icon: 'file-signature',
                onClick: (article: DossierArticle) => {
                    enqueueSnackbar(`Non implémenté signature ${article.label}`, {
                        variant: 'error',
                    });
                },
            });

            actions.push({
                label: 'Activer option DCE',
                icon: 'check-square',
                onClick: (article: DossierArticle) => {
                    enqueueSnackbar(`Non implémenté option DCE ${article.label}`, {
                        variant: 'error',
                    });
                },
            });

            actions.push(
                actionUpdateValidatorStatus(() => {
                    const process = findProcessByArticle(dossier, [validatorTypeId], item.id);
                    let currentStatus = { value: '', label: '' };
                    if (process) {
                        currentStatus = {
                            value: process.statusCode,
                            label: process.statusLabel,
                        };
                    }
                    modalActions.call(
                        <UpdateValidatorForm
                            validatorTypeId={validatorTypeId}
                            processing={processing}
                            article={item}
                            dossier={dossier}
                            onItemSelected={() => {
                                modalActions.close();
                                refreshDossier();
                            }}
                            currentStatus={currentStatus}
                        />,
                    );
                }),
            );
        }
        return actions;
    };

    const onSubmitAddTax = async (data: AddTaxFormData) => {
        setProcessing(true);

        try {
            const articleId = await addArticle({
                prestationId: data.prestationId,
                label: data.title,
                factArticleId: data.articleId,
            } as AddArticleParamsType);
            await createValidatorProcess(dossier.id as number, [articleId]);
            modalActions.close();
            refreshDossier();
        } catch (err) {
            apiErrorMessageSnackbar(
                err as Error,
                "Une erreur est survenue lors de l'ajout de l'enregistrement",
            );
        } finally {
            setProcessing(false);
        }
    };

    const handleAddTax = useCallback(() => {
        modalActions.call(
            <AddTaxForm
                onCancel={() => {
                    setProcessing(false);
                    modalActions.close();
                }}
                onSubmit={onSubmitAddTax}
                dossier={dossier}
            />,
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dossier, modalActions, processing]);

    // Stand by
    // const handleMultipleArticleUpdateValidator = useCallback(() => {
    //     modalActions.call(
    //         <MultipleArticleUpdateValidatorForm
    //             articles={blocArticles}
    //             validatorId={validatorId}
    //             processing={processing}
    //             dossier={dossier}
    //             onItemSelected={() => {}}
    //         />,
    //     );
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [dossier, modalActions, processing]);

    return (
        <Accordion
            title="Enregistrements impôts"
            expandLabel={getArticleProcessesStatusLabel(status)}
            expanded
            expandColor={getArticleProcessesStatusColor(status)}
            sx={{ backgroundColor: palette.background.paper }}
            contentSxProps={{ gap: '16px', display: 'grid' }}
        >
            {articles.length > 0 && (
                <DossierArticlesProcessesTaxList
                    articles={articles}
                    dossier={dossier}
                    validatorTypeId={validatorTypeId}
                    legalAnnoucements={[]}
                    getItemActions={getItemActions}
                />
            )}

            {canCreateProcess && (
                <DossierProcessesFooter
                    handleAddItem={handleAddTax}
                    // handleMainAction={handleMultipleArticleUpdateValidator}
                    addItemTextBtn="Ajouter un enregistrement"
                    // mainActionTextBtn="Lier à des valideurs"
                    canAddItem
                    // canHandleMainAction
                />
            )}
        </Accordion>
    );
};

export default DossierImpotProcesses;
